import { useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { communityChannel } from '~publish/legacy/routes'

import { filterQueryParamsByRoute } from './utils/filterQueryParamsByRoute'
import { getCurrentCommunityView } from './utils/getCurrentCommunityView'

/**
 * Valid query params for community channel routes
 * Matches the queryParams defined in routes/index.ts for communityChannel
 */
const COMMUNITY_QUERY_PARAMS = communityChannel.queryParams

/**
 * Build community channel URL with preserved filter params (status, tab, sort, publishedAt)
 *
 * This hook ensures that when navigating between channels in the Community section (new IA overhaul),
 * the selected filters (like status=dismissed, status=all, etc.) persist in the URL.
 * It also preserves the current view (list vs by-post) across channel navigation.
 *
 * @param channelId - The ID of the channel to navigate to
 * @returns URL string with preserved query parameters
 *
 * @example
 * ```tsx
 * const commentsUrl = useCommentsChannelUrl(channel.id)
 * // If current URL has ?status=dismissed, returns: /channels/123/community?status=dismissed
 * ```
 */
export function useCommentsChannelUrl(channelId: string): string {
  const location = useLocation()
  const currentSearch = parseQueryParams(location.search)
  const currentView = getCurrentCommunityView(location.pathname)

  // Filter current search params to only include those valid for community channel routes
  const filteredParams = filterQueryParamsByRoute(
    currentSearch,
    COMMUNITY_QUERY_PARAMS,
  )

  const queryString = serializeQueryParams(filteredParams)
  const viewSegment = currentView === 'comments' ? '' : '/posts'
  return `/channels/${channelId}/community${viewSegment}${
    queryString ? `?${queryString}` : ''
  }`
}
